import { all, call, cancelled, put, takeLatest } from "redux-saga/effects";
import { assertSuccess, normalizeError, request } from "../../helpers/request";
import { server } from "../globalSettings";

let sequence = 0;
const idle = () => ({ data: null, loading: false, status: "idle", error: null, requestId: null });
const evaluate = (value, params) => typeof value === "function" ? value(params) : value;
const itemPath = endpoint => params => {
  if (params.id === undefined || params.id === null || params.id === "") {
    throw new Error("An id is required for this operation.");
  }
  return `${endpoint}/${encodeURIComponent(params.id)}`;
};

export function authHeaders() {
  const stored = localStorage.getItem("token");
  const token = stored ? JSON.parse(stored) : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Generates actions, API methods, reducer, selectors and a root saga. */
export default function createCrudModule({
  name, endpoint, stateKey = name, baseUrl = server, headers = authHeaders,
  operations = {}, isSuccess,
}) {
  if (!name || !endpoint) throw new Error("A CRUD module requires name and endpoint.");
  const definitions = {
    list: { method: "GET", path: endpoint, query: params => params, select: value => value?.data },
    details: { method: "GET", path: itemPath(endpoint), select: value => value?.data },
    create: { method: "POST", path: endpoint, body: params => params },
    update: { method: "PUT", path: itemPath(endpoint), body: params => params.data },
    delete: { method: "DELETE", path: itemPath(endpoint) },
  };
  Object.entries(operations).forEach(([key, config]) => {
    if (key === "reset") throw new Error("reset is reserved for resetting module state.");
    if (config === false) delete definitions[key];
    else definitions[key] = { ...definitions[key], ...config };
  });
  const actionTypes = {};
  const actions = {};
  const api = {};
  const handlers = {};
  const initialState = () => Object.fromEntries(Object.keys(definitions).map(key => [key, idle()]));
  const resetType = `crud/${name}/reset`;
  actions.reset = operation => ({ type: resetType, payload: operation });

  Object.entries(definitions).forEach(([operation, config]) => {
    const prefix = `crud/${name}/${operation}`;
    const types = actionTypes[operation] = {
      request: `${prefix}/request`, success: `${prefix}/success`,
      failure: `${prefix}/failure`, cancel: `${prefix}/cancel`,
    };
    actions[operation] = (params = {}) => ({
      type: types.request, payload: params, meta: { requestId: `${name}:${++sequence}` },
    });
    api[operation] = async (params = {}) => {
      if (config.api) {
        const value = assertSuccess(await config.api(params), config.isSuccess || isSuccess);
        return config.select ? config.select(value) : value;
      }
      const path = evaluate(config.path, params);
      const query = evaluate(config.query, params);
      const search = new URLSearchParams();
      Object.entries(query || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          (Array.isArray(value) ? value : [value]).forEach(entry => search.append(key, entry));
        }
      });
      let url = /^https?:\/\//i.test(path) ? path : `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
      if (search.toString()) url += `${url.includes("?") ? "&" : "?"}${search}`;
      const body = evaluate(config.body, params);
      const isForm = typeof FormData !== "undefined" && body instanceof FormData;
      return request(url, {
        method: evaluate(config.method, params) || "GET",
        headers: {
          ...(body !== undefined && !isForm ? { "Content-Type": "application/json" } : {}),
          ...evaluate(headers, params), ...evaluate(config.headers, params),
        },
        ...(body !== undefined ? { body: isForm ? body : JSON.stringify(body) } : {}),
      }, { isSuccess: config.isSuccess || isSuccess, select: config.select, responseType: config.responseType });
    };
    handlers[types.request] = (state, action) => ({ ...state, [operation]: {
      ...state[operation], loading: true, status: "loading", error: null, requestId: action.meta.requestId,
    } });
    ["success", "failure", "cancel"].forEach(result => {
      handlers[types[result]] = (state, action) => {
        if (state[operation].requestId !== action.meta.requestId) return state;
        return { ...state, [operation]: {
          ...state[operation], loading: false,
          status: result === "failure" ? "error" : result === "cancel" ? "idle" : "success",
          ...(result === "success" ? { data: action.payload, error: null } : {}),
          ...(result === "failure" ? { error: action.payload } : {}),
        } };
      };
    });
  });

  function reducer(state = initialState(), action) {
    if (action.type === resetType) {
      if (action.payload === undefined) return initialState();
      return definitions[action.payload] ? { ...state, [action.payload]: idle() } : state;
    }
    return handlers[action.type] ? handlers[action.type](state, action) : state;
  }

  function* saga() {
    yield all(Object.keys(definitions).map(operation => takeLatest(actionTypes[operation].request, function* (action) {
      try {
        const data = yield call(api[operation], action.payload);
        yield put({ type: actionTypes[operation].success, payload: data, meta: action.meta });
      } catch (error) {
        yield put({ type: actionTypes[operation].failure, payload: normalizeError(error), meta: action.meta });
      } finally {
        if (yield cancelled()) yield put({ type: actionTypes[operation].cancel, meta: action.meta });
      }
    })));
  }

  const emptyState = initialState();
  const selectState = state => state[stateKey] || emptyState;
  return { name, stateKey, actionTypes, actions, api, reducer, saga, selectState };
}
